import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useContext, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate, Link as RouterLink, useSearchParams, Navigate } from 'react-router-dom';
import { z } from 'zod';
import {
  AuthService,
  RegistrationStatusEnum,
  SourceTypeEnum,
  UserCompleteRegistration,
  UserDetail,
  UserRegistration,
  UserTypeEnum,
} from '../../../../../js/api';
import { FormCheckboxField } from '../../../../../js/app/components/FormCheckboxField';
import { FormFeedback } from '../../../../../js/app/components/FormFeedback';
import { FormInputMaskField } from '../../../../../js/app/components/FormInputMaskField';
import { FormTextField } from '../../../../../js/app/components/FormTextField';
import { HiddenUsernameField } from '../../../../../js/app/components/HiddenUsernameField';
import { Logo } from '../../../../../js/app/components/Logo';
import { PasswordField } from '../../../../../js/app/components/PasswordField';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { userQueryKeys } from '../../../../../js/app/query-keys-factories/user';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { strongPasswordZodSchema } from '../../../../../js/utils/security';
import {
  clearSourcedRegistrationHandoff,
  readSourcedRegistrationHandoff,
  writeSourcedRegistrationHandoff,
} from '../../../../../js/utils/sourcedRegistrationHandoff';
import { Button } from '../../../components/button';
import { Spinner } from '../../../components/spinner';

const detailFields = {
  first_name: z.string().min(1, { message: 'First Name is required' }),
  last_name: z.string().min(1, { message: 'Last Name is required' }),
  phone_number: z.string().min(1, { message: 'Phone Number is required' }),
  business_name: z.string().min(1, { message: 'Business Name is required' }),
  text_opt_in: z.boolean(),
};

// Used when the user arrives already signed in through Google: there is no
// password to set, so requiring one would block them.
const finishModeSchema = z.object(detailFields);

const passwordModeSchema = z
  .object({
    ...detailFields,
    password1: strongPasswordZodSchema,
    password2: z.string().min(1, { message: 'Confirm Password is required' }),
  })
  .refine(({ password1, password2 }) => password1 === password2, {
    path: ['password2'],
    message: "Your passwords don't match.",
  });

// Everything asked for before the user picks how they'll sign in. Passwords are
// deliberately excluded — they only apply to one of the two credential choices.
const DETAIL_FIELDS = [
  'first_name',
  'last_name',
  'phone_number',
  'business_name',
  'text_opt_in',
] as const;

/** The card chrome shared by every state of this page. */
const RegistrationShell = ({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) => (
  <div className="tw:flex-1 tw:w-full tw:min-h-screen tw:flex tw:flex-col tw:p-4 tw:pt-3 tw:sm:bg-brand-green-0 tw:sm:flex-row tw:sm:items-center tw:sm:justify-center tw:sm:p-8">
    <div className="tw:w-full tw:sm:max-w-md tw:sm:mx-auto">
      <div className="tw:bg-zinc-50 tw:rounded-md tw:sm:border tw:sm:border-zinc-200 tw:sm:shadow-sm tw:sm:p-6">
        <div className="tw:flex tw:justify-center tw:mb-4">
          <Logo className="logo" variant="color" />
        </div>
        <h5 className="tw:text-center tw:mb-2 tw:pb-2 text-color-primary-dark">{heading}</h5>
        {children}
      </div>
    </div>
  </div>
);

const PREFILLABLE_FIELDS = ['first_name', 'last_name', 'phone_number', 'business_name'] as const;

/**
 * Seeds the form from the invitation link, then from anything the user already
 * typed before a Google attempt that didn't complete — so coming back here (from
 * the "wrong account" screen, or by reopening the invite email) never means
 * filling the form out twice. Typed values win over link params.
 */
const usePrefillFromInviteParams = (
  searchParams: URLSearchParams,
  email: string | null,
  setValue: (field: string, value: string | boolean, options?: object) => void
) => {
  useEffect(() => {
    const options = { shouldValidate: false, shouldDirty: true };
    PREFILLABLE_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value?.trim()) {
        setValue(field, value, options);
      }
    });

    const handoff = readSourcedRegistrationHandoff(email);
    if (!handoff) {
      return;
    }
    PREFILLABLE_FIELDS.forEach((field) => {
      const value = handoff[field];
      if (value?.trim()) {
        setValue(field, value, options);
      }
    });
    setValue('text_opt_in', !!handoff.text_opt_in, options);
  }, [searchParams, email, setValue]);
};

/**
 * In finish mode the account already holds what the organisation supplied, so
 * seed the form from it — including the business name, which is why this page is
 * the right home for 1099 completion rather than the generic signup form.
 */
const usePrefillFromAccount = (
  user: UserDetail | undefined,
  isFinishMode: boolean,
  setValue: (field: string, value: string, options?: object) => void
) => {
  useEffect(() => {
    if (!isFinishMode || !user) {
      return;
    }
    const options = { shouldValidate: false, shouldDirty: true };
    const seed: Array<[string, string | undefined | null]> = [
      ['first_name', user.first_name],
      ['last_name', user.last_name],
      ['phone_number', user.phone_number],
      ['business_name', user.business_name],
    ];
    seed.forEach(([field, value]) => {
      if (value?.trim()) {
        setValue(field, value, options);
      }
    });
  }, [user, isFinishMode, setValue]);
};

/**
 * Hands off to Google, carrying the details already entered so the completion
 * step can finish registration instead of asking for them again.
 *
 * `expected_email` pins the sign-in to the invited address. Without it Google
 * silently reuses whichever account the browser is signed into, which links and
 * signs in the wrong person and leaves the invitation unclaimed.
 */
const startGoogleSignUp = (
  email: string,
  next: string | null,
  values: Record<string, string | boolean>
) => {
  writeSourcedRegistrationHandoff({
    email,
    first_name: values.first_name as string,
    last_name: values.last_name as string,
    phone_number: values.phone_number as string,
    business_name: values.business_name as string,
    text_opt_in: !!values.text_opt_in,
    next,
  });
  const params = new URLSearchParams({
    user_type: UserTypeEnum.PROVIDER,
    expected_email: email,
  });
  window.location.href = `${window.GOOGLE_LOGIN_REDIRECT_URL}?${params.toString()}`;
};

export const SourcedProviderRegistration = () => {
  useDocumentTitle('Complete Registration');
  // isInitialLoading rather than isLoading for the same v4 reason as below: on a
  // page with no session cookie the current-user query is disabled, and
  // isLoading would never clear.
  const { setSessionExists, user, isInitialLoading: isSessionLoading } = useContext(SessionContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // "Finish" mode: they came back from Google already signed in, so the identity
  // exists and only the profile is outstanding. Same page, same fields — the
  // credential step becomes a confirmation and submit completes registration
  // rather than creating an account.
  const isFinishMode =
    user?.registration_status === RegistrationStatusEnum.MISSING_INFO &&
    user?.source_type === SourceTypeEnum.ORGANIZATION;

  const email = searchParams.get('email') ?? user?.email ?? null;
  const next = searchParams.get('next');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const loginHref = next ? `/login?next=${encodeURIComponent(next)}` : '/login';

  // Details are collected before the credential so that choosing Google never
  // discards anything already typed, and so Google finishes the registration
  // rather than handing the user a second form.
  const [step, setStep] = useState<'details' | 'credential'>('details');

  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    setValue,
    trigger,
    getValues,
  } = useForm({
    resolver: zodResolver(isFinishMode ? finishModeSchema : passwordModeSchema),
  });

  const goToCredentialStep = async () => {
    const detailsAreValid = await trigger(DETAIL_FIELDS);
    if (detailsAreValid) {
      setStep('credential');
    }
  };

  const mutationRegister = useMutation({
    mutationFn: async (data: UserRegistration) => {
      return AuthService.authRegistrationCreate({ requestBody: data });
    },
  });

  const mutationCompleteRegistration = useMutation({
    mutationFn: async (data: UserCompleteRegistration) => {
      return AuthService.authRegistrationCompleteUpdate({ requestBody: data });
    },
    onSuccess: async () => {
      // Every route guard reads registration_status off the cached current user
      // (AuthenticatedRoute bounces MISSING_INFO to /register/complete, which
      // sends sourced providers straight back here). Without refreshing it, a
      // successful completion loops the user back to this form. Awaited so the
      // refetch lands before we navigate.
      await queryClient.invalidateQueries({ queryKey: userQueryKeys.all() });
    },
  });

  // Binary eligibility check — false means the invite was already completed (or
  // never existed), so don't let the user fill the form just to get rejected.
  // On query error we fail open: the submit-time handling still catches it.
  // Skipped in finish mode: the account demonstrably exists (they're signed into
  // it), so an eligibility answer of "no" would wrongly show them the
  // already-registered card instead of letting them finish.
  // `isInitialLoading`, not `isLoading`: in react-query v4 a disabled query
  // reports isLoading:true forever (it never fetches, so it never leaves the
  // loading status). Gating the spinner on that left finish mode — where this
  // query is deliberately disabled — spinning permanently.
  const { data: eligibility, isInitialLoading: eligibilityLoading } = useQuery({
    queryKey: ['sourcedRegistrationEligibility', email],
    queryFn: () =>
      AuthService.authRegistrationSourcedEligibilityRetrieve({ email: email as string }),
    enabled: !!email && !isFinishMode,
    retry: false,
  });

  usePrefillFromInviteParams(searchParams, email, setValue);
  usePrefillFromAccount(user, isFinishMode, setValue);

  if (!email) {
    // In the signed-in path the address comes from the account, so don't bounce
    // to generic signup before the current-user query has resolved.
    if (isSessionLoading) {
      return (
        <RegistrationShell heading="Complete Your Registration">
          <div className="tw:flex tw:justify-center tw:py-8">
            <Spinner variant="zinc" />
          </div>
        </RegistrationShell>
      );
    }
    return <Navigate to="/register/provider/setup" replace />;
  }

  const showAlreadyRegistered = alreadyRegistered || eligibility?.can_register === false;

  const handleGoogleSignUp = () => startGoogleSignUp(email, next, getValues());

  // Already authenticated, so there is nothing to register — just finish the
  // profile. No recaptcha either: the session is the proof of humanity.
  const finishRegistration = async (data) => {
    try {
      await mutationCompleteRegistration.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        business_name: data.business_name,
        text_opt_in: !!data.text_opt_in,
      });
      clearSourcedRegistrationHandoff();
      navigate(next ?? '/app');
    } catch (error) {
      setFormErrorsFromApiErrors(error as any, setError);
    }
  };

  const onSubmit = async (data) => {
    if (isFinishMode) {
      return finishRegistration(data);
    }
    try {
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('registration');
      }

      const response = await mutationRegister.mutateAsync({
        ...data,
        email,
        user_type: UserTypeEnum.PROVIDER,
        recaptcha_token: recaptchaToken,
      } as UserRegistration);

      if (response?.id) {
        setSessionExists();
        navigate(next ?? '/app');
      } else {
        navigate('/register/email-confirmation');
      }
    } catch (error) {
      const emailErrors = (error as any)?.body?.email;
      if (
        Array.isArray(emailErrors) &&
        emailErrors.some((message) => /already registered/i.test(message))
      ) {
        setAlreadyRegistered(true);
        return;
      }
      setFormErrorsFromApiErrors(error as any, setError);
    }
  };

  if (eligibilityLoading) {
    return (
      <RegistrationShell heading="Complete Your Registration">
        <div className="tw:flex tw:justify-center tw:py-8">
          <Spinner variant="zinc" />
        </div>
      </RegistrationShell>
    );
  }

  if (showAlreadyRegistered) {
    return (
      <RegistrationShell heading="Account Already Registered">
        <div className="tw:flex tw:flex-col tw:gap-4">
          <p className="tw:text-center tw:text-zinc-700">
            An account is already registered with <span className="tw:font-medium">{email}</span>.
            Please log in to continue.
          </p>
          <Button
            color="brandgreen1"
            className="tw:w-full tw:bg-teal-600 tw:text-white tw:py-3 tw:px-4 tw:rounded-lg tw:text-lg tw:font-medium tw:cursor-pointer hover:tw:bg-teal-700"
            onClick={() => navigate(loginHref)}
          >
            Go to Log In
          </Button>
        </div>
      </RegistrationShell>
    );
  }

  return (
    <RegistrationShell heading="Complete Your Registration">
      <>
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          className="tw:flex tw:flex-col new-account-form"
        >
          <FormFeedback serverErrors={(mutationRegister.error as any)?.body?.non_field_errors} />
          <FormFeedback clientError={(errors as any).root?.non_field_errors} />
          <FormFeedback
            clientError={
              (errors as any).recaptcha_token
                ? {
                    ...(errors as any).recaptcha_token,
                    message: `reCAPTCHA: ${(errors as any).recaptcha_token.message}`,
                  }
                : undefined
            }
          />
          <div className="tw:mb-3">
            <div className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1">Email</div>
            <div
              className={`tw:flex tw:items-center tw:justify-between tw:bg-zinc-100 tw:border tw:rounded-md tw:px-3 tw:py-2 ${
                (errors as any).email ? 'tw:border-red-500' : 'tw:border-zinc-200'
              }`}
            >
              <span className="tw:text-zinc-700">{email}</span>
              <RouterLink
                to="/register/provider/setup"
                className="tw:text-xs tw:text-teal-600 hover:tw:underline"
              >
                Not you?
              </RouterLink>
            </div>
            <FormFeedback clientError={(errors as any).email} />
          </div>
          {/* Kept mounted across both steps so react-hook-form retains the
                    values; hidden rather than unmounted. */}
          <div className={step === 'details' ? '' : 'tw:hidden'}>
            <div className="tw:grid tw:grid-cols-2 tw:gap-4">
              <FormTextField
                name="first_name"
                label="First Name"
                errors={errors}
                register={register}
                required
              />
              <FormTextField
                name="last_name"
                label="Last Name"
                errors={errors}
                register={register}
                required
              />
            </div>
            <FormInputMaskField
              name="phone_number"
              label="Phone Number"
              mask="(___) ___-____"
              replacement={{ _: /\d/ }}
              placeholder="(123) 456-7890"
              errors={errors}
              register={register}
              required
            />
            <FormTextField
              name="business_name"
              label="Business Name"
              errors={errors}
              register={register}
              required
            />
            <div className="tw:flex tw:items-start tw:mb-2">
              <FormCheckboxField
                name="text_opt_in"
                label=""
                errors={errors}
                register={register}
                className="tw:mb-0 tw:shrink-0 tw:inline-flex"
              />
              <small className="tw:text-zinc-500 tw:text-sm">
                I consent to receive notifications from Sendit about account activity via SMS.
                Message & data rates may apply. Message frequency depends on account activity. Reply
                STOP to opt out, HELP for help.{' '}
                <a
                  href="https://www.senditgigs.com/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>{' '}
                &{' '}
                <a
                  href="https://www.senditgigs.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </small>
            </div>
            <div className="tw:mt-2">
              <Button
                type="button"
                color="brandgreen1"
                className="tw:w-full tw:bg-teal-600 tw:text-white tw:py-3 tw:px-4 tw:rounded-lg tw:text-lg tw:font-medium tw:cursor-pointer hover:tw:bg-teal-700"
                onClick={goToCredentialStep}
              >
                Continue
              </Button>
            </div>
          </div>

          {step === 'credential' && (
            <>
              {isFinishMode ? (
                <div className="tw:flex tw:items-center tw:gap-2 tw:mb-4 tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-white tw:px-3 tw:py-2">
                  <FcGoogle />
                  <span className="tw:text-sm tw:text-zinc-700">
                    Signed in with Google as <span className="tw:font-medium">{email}</span>
                  </span>
                </div>
              ) : (
                <>
                  <p className="tw:text-sm tw:text-zinc-600 tw:mb-3">
                    Last step — choose how you&apos;ll sign in.
                  </p>
                  <button
                    type="button"
                    onClick={handleGoogleSignUp}
                    className="tw:w-full tw:flex tw:items-center tw:justify-center tw:gap-2 tw:border tw:border-zinc-300 tw:bg-white tw:text-zinc-700 tw:py-3 tw:px-4 tw:rounded-lg tw:text-base tw:font-medium tw:cursor-pointer hover:tw:bg-zinc-50"
                  >
                    <FcGoogle />
                    <span>Continue with Google</span>
                  </button>
                  <div className="tw:flex tw:items-center tw:gap-3 tw:my-4">
                    <span className="tw:h-px tw:flex-1 tw:bg-zinc-200" />
                    <span className="tw:text-xs tw:text-zinc-500">or set a password</span>
                    <span className="tw:h-px tw:flex-1 tw:bg-zinc-200" />
                  </div>
                  <HiddenUsernameField email={email} />
                  <PasswordField
                    name="password1"
                    label="Password"
                    errors={errors}
                    register={register}
                    autoComplete="new-password"
                    required
                  />
                  <PasswordField
                    name="password2"
                    label="Confirm Password"
                    errors={errors}
                    register={register}
                    autoComplete="new-password"
                    required
                  />
                </>
              )}
              <div className="tw:mt-2">
                <Button
                  type="submit"
                  color="brandgreen1"
                  className="tw:w-full tw:bg-teal-600 tw:text-white tw:py-3 tw:px-4 tw:rounded-lg tw:text-lg tw:font-medium tw:cursor-pointer hover:tw:bg-teal-700 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                  disabled={mutationRegister.isLoading}
                >
                  Complete Registration
                </Button>
              </div>
              <button
                type="button"
                onClick={() => setStep('details')}
                className="tw:!mt-3 tw:text-sm tw:text-brand-green-1 tw:cursor-pointer hover:tw:underline"
              >
                Back to your details
              </button>
            </>
          )}
        </form>
      </>
    </RegistrationShell>
  );
};
